/**
 * Add / edit / remove a special date. Adults only — the page never shows this to a
 * kid, and worker/routes/birthdays.ts re-checks on every mutation regardless.
 *
 * The month + day are picked from selects (no ambiguous free-typed dates), and the
 * day list follows the chosen month and year, so Feb never offers the 30th. Year is
 * optional; a date with no year simply has no age count. A date can optionally be
 * tied to a household member — that roster is adult-gated (/api/family), which is
 * fine here because only adults open this form; if it can't be read the picker just
 * hides and everything else still saves.
 *
 * A failed save NEVER clears the fields — the error shows and the words stay put.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { api } from '../../lib/api';
import type { FamilyMember, SpecialDate, SpecialDateInput } from '../../shared/types';
import {
  Modal,
  btnDanger,
  btnPrimary,
  btnSecondary,
  cx,
  daysInMonth,
  fieldInput,
  fieldLabel,
  monthName,
  explainError,
} from './shared';

const KINDS: { value: SpecialDate['kind']; label: string }[] = [
  { value: 'birthday', label: 'Birthday' },
  { value: 'anniversary', label: 'Anniversary' },
  { value: 'other', label: 'Other' },
];

export default function BirthdayForm({
  date,
  onClose,
  onSaved,
}: {
  date: SpecialDate | null;
  onClose: () => void;
  onSaved: (message: string) => Promise<void>;
}) {
  const editing = date != null;

  const [name, setName] = useState(date?.name ?? '');
  const [kind, setKind] = useState<SpecialDate['kind']>(date?.kind ?? 'birthday');
  const [month, setMonth] = useState<number>(date?.month ?? new Date().getMonth() + 1);
  const [day, setDay] = useState<number>(date?.day ?? 1);
  const [year, setYear] = useState(date?.year != null ? String(date.year) : '');
  const [memberId, setMemberId] = useState(date?.member_id != null ? String(date.member_id) : '');
  const [notes, setNotes] = useState(date?.notes ?? '');

  const [family, setFamily] = useState<FamilyMember[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Optional member link. Adults can read the roster; if it fails, we just don't
  // offer the picker (a made-up list would be worse than none).
  useEffect(() => {
    let cancelled = false;
    api
      .get<FamilyMember[]>('/family')
      .then((rows) => {
        if (!cancelled) setFamily(rows.filter((m) => m.active !== false));
      })
      .catch(() => {
        if (!cancelled) setFamily([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Day options follow the month (and year, for February). Keep the chosen day valid
  // when the month changes so we never submit, say, Feb 30.
  const yearNum = /^\d{4}$/.test(year.trim()) ? Number(year.trim()) : 2000; // leap fallback
  const maxDay = daysInMonth(yearNum, month);
  const boundedDay = Math.min(day, maxDay);
  useEffect(() => {
    if (day > maxDay) setDay(maxDay);
  }, [day, maxDay]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    const trimmed = name.trim();
    if (!trimmed) {
      setError('Give this date a name.');
      return;
    }

    const yearTrimmed = year.trim();
    let yearValue: number | null = null;
    if (yearTrimmed !== '') {
      if (!/^\d{4}$/.test(yearTrimmed)) {
        setError('Year should be four digits, like 1985 — or leave it blank.');
        return;
      }
      yearValue = Number(yearTrimmed);
    }

    const payload: SpecialDateInput = {
      name: trimmed,
      kind,
      month,
      day: boundedDay,
      year: yearValue,
      member_id: memberId === '' ? null : Number(memberId),
      notes: notes.trim() || null,
    };

    setSaving(true);
    try {
      if (editing) await api.put<SpecialDate>(`/birthdays/${date.id}`, payload);
      else await api.post<SpecialDate>('/birthdays', payload);
      await onSaved(editing ? 'Saved.' : `Added ${trimmed}.`);
      onClose();
    } catch (err) {
      setError(explainError(err, 'That did not save. Try again in a second.'));
      setSaving(false);
    }
  }

  async function remove() {
    if (!editing) return;
    setError(null);
    setDeleting(true);
    try {
      await api.del<SpecialDate>(`/birthdays/${date.id}`);
      await onSaved(`Removed ${date.name}.`);
      onClose();
    } catch (err) {
      setError(explainError(err, 'Could not remove that. Try again.'));
      setDeleting(false);
      setConfirmingDelete(false);
    }
  }

  const busy = saving || deleting;

  return (
    <Modal title={editing ? 'Edit date' : 'Add a date'} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label htmlFor="sd-name" className={fieldLabel}>
            Whose day is it
          </label>
          <input
            id="sd-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={fieldInput}
            placeholder="Grandma Jo"
            maxLength={80}
            required
            autoFocus
          />
        </div>

        <fieldset>
          <legend className={fieldLabel}>What kind</legend>
          <div className="mt-2 grid grid-cols-3 gap-2">
            {KINDS.map((k) => (
              <label
                key={k.value}
                className={cx(
                  'flex min-h-[44px] cursor-pointer items-center justify-center rounded-xl border px-2 text-sm font-medium',
                  kind === k.value
                    ? 'border-sage bg-sage-soft text-sage'
                    : 'border-border bg-surface text-ink hover:bg-surface-sunk'
                )}
              >
                <input
                  type="radio"
                  name="kind"
                  value={k.value}
                  checked={kind === k.value}
                  onChange={() => setKind(k.value)}
                  className="sr-only"
                />
                {k.label}
              </label>
            ))}
          </div>
        </fieldset>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="sd-month" className={fieldLabel}>
              Month
            </label>
            <select
              id="sd-month"
              value={String(month)}
              onChange={(e) => setMonth(Number(e.target.value))}
              className={fieldInput}
            >
              {Array.from({ length: 12 }, (_, i) => (
                <option key={i + 1} value={String(i + 1)}>
                  {monthName(i + 1)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="sd-day" className={fieldLabel}>
              Day
            </label>
            <select
              id="sd-day"
              value={String(boundedDay)}
              onChange={(e) => setDay(Number(e.target.value))}
              className={fieldInput}
            >
              {Array.from({ length: maxDay }, (_, i) => (
                <option key={i + 1} value={String(i + 1)}>
                  {i + 1}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label htmlFor="sd-year" className={fieldLabel}>
            Year <span className="font-normal text-ink-faint">(optional — adds an age)</span>
          </label>
          <input
            id="sd-year"
            value={year}
            onChange={(e) => setYear(e.target.value)}
            className={fieldInput}
            placeholder="1985"
            inputMode="numeric"
            maxLength={4}
            aria-describedby="sd-year-hint"
          />
          <p id="sd-year-hint" className="mt-1 text-xs text-ink-faint">
            Leave it blank if you&rsquo;d rather not track the age.
          </p>
        </div>

        {family && family.length > 0 && (
          <div>
            <label htmlFor="sd-member" className={fieldLabel}>
              In the family? <span className="font-normal text-ink-faint">(optional)</span>
            </label>
            <select
              id="sd-member"
              value={memberId}
              onChange={(e) => setMemberId(e.target.value)}
              className={fieldInput}
            >
              <option value="">Not a household member</option>
              {family.map((m) => (
                <option key={m.id} value={String(m.id)}>
                  {m.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label htmlFor="sd-notes" className={fieldLabel}>
            Notes <span className="font-normal text-ink-faint">(optional)</span>
          </label>
          <textarea
            id="sd-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className={cx(fieldInput, 'min-h-[72px]')}
            placeholder="Loves lemon cake. Gift ideas: gardening tools."
            rows={2}
            maxLength={500}
          />
        </div>

        {error ? (
          <p role="alert" className="rounded-xl bg-clay-soft px-3 py-2 text-sm text-clay">
            {error}
          </p>
        ) : null}

        <div className="flex gap-2 pt-1">
          <button type="submit" disabled={busy} className={cx(btnPrimary, 'flex-1')}>
            {saving ? 'Saving…' : editing ? 'Save changes' : 'Add date'}
          </button>
          <button type="button" onClick={onClose} disabled={busy} className={btnSecondary}>
            Cancel
          </button>
        </div>

        {editing &&
          (confirmingDelete ? (
            <div className="rounded-xl border border-over/40 bg-clay-soft p-3">
              <p className="text-sm text-ink">
                Remove <span className="font-medium">{date.name}</span> from the list?
              </p>
              <div className="mt-3 flex gap-2">
                <button type="button" onClick={remove} disabled={busy} className={btnDanger}>
                  {deleting ? 'Removing…' : 'Yes, remove it'}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmingDelete(false)}
                  disabled={busy}
                  className={btnSecondary}
                >
                  Keep it
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmingDelete(true)}
              disabled={busy}
              className="text-sm font-medium text-clay hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-clay"
            >
              Remove this date
            </button>
          ))}
      </form>
    </Modal>
  );
}
